// Shared adaptive-sampling campaign state.
//
// The Mannequin / Tailor-vs-LHS sampler is configured and launched on the
// dedicated Training surface, but the composite ADB explorer wants to overlay
// the campaign's proposed points on its real plot. Those two live on different
// V5 surfaces (only one is mounted at a time), so the *selection* (which
// campaign is active) has to live above both — here, in a context mounted in
// the workbench.
//
// The mock campaign runtime itself is module-level (see mock/wsSimulator.ts),
// so a campaign keeps streaming in `campaignStore` even while the builder is
// unmounted; the explorer just needs the campaign id to read it back.

import { createContext, useContext, useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

import { USE_MOCK, useCampaignStateQuery, useCampaignStream } from "./api";
import type { CampaignState, SamplePoint } from "./types";

export type AdaptiveCampaignSelection = {
  /** Project the campaign was launched under. The explorer only overlays a
   * campaign whose projectId matches the project it is viewing, so a campaign
   * run on one project never leaks onto another. */
  projectId?: string;
  /** The Tailor (adaptive) campaign whose points the explorer overlays. */
  tailorId?: string;
  /** The LHS baseline campaign (Training-page comparison only). */
  lhsId?: string;
  /** Human label for the active oracle (e.g. "Synthetic Cessna-like"). */
  aircraftLabel?: string;
  /** Last Training-page sample adopted into the ADB Explorer's held slice. */
  adoptedInputs?: Record<string, number>;
};

type AdaptiveCampaignValue = {
  selection: AdaptiveCampaignSelection;
  setSelection: Dispatch<SetStateAction<AdaptiveCampaignSelection>>;
  clearSelection: () => void;
};

const AdaptiveCampaignContext = createContext<AdaptiveCampaignValue>({
  selection: {},
  setSelection: () => {},
  clearSelection: () => {},
});

export function AdaptiveCampaignProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<AdaptiveCampaignSelection>({});
  const value = useMemo<AdaptiveCampaignValue>(
    () => ({ selection, setSelection, clearSelection: () => setSelection({}) }),
    [selection],
  );
  return <AdaptiveCampaignContext.Provider value={value}>{children}</AdaptiveCampaignContext.Provider>;
}

export function useAdaptiveCampaign(): AdaptiveCampaignValue {
  return useContext(AdaptiveCampaignContext);
}

type AdaptiveSamplingDebugWindow = Window & {
  __ADAPTIVE_SAMPLING_DEBUG__?: { getCampaign?: (id: string) => CampaignState | undefined };
};

function readMockCampaignState(id: string | undefined): CampaignState | undefined {
  if (!id || !USE_MOCK || typeof window === "undefined") return undefined;
  return (window as AdaptiveSamplingDebugWindow).__ADAPTIVE_SAMPLING_DEBUG__?.getCampaign?.(id);
}

/**
 * Subscribe to a campaign and return its points (latest iteration first),
 * re-reading the module-level mock store on a timer so streaming points show
 * up even when this consumer didn't create the campaign. Safe with an
 * undefined id (returns []).
 */
export function useCampaignPoints(campaignId: string | undefined): SamplePoint[] {
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    if (!campaignId || !USE_MOCK) return;
    const timer = window.setInterval(() => setPulse((value) => (value + 1) % 1_000_000), 500);
    return () => window.clearInterval(timer);
  }, [campaignId]);

  useCampaignStream(campaignId);
  const stateQuery = useCampaignStateQuery(campaignId, {
    refetchInterval: campaignId ? 1_000 : false,
    staleTime: 400,
  });

  const mockState = useMemo(() => readMockCampaignState(campaignId), [campaignId, pulse]);
  const state = campaignId ? (mockState ?? stateQuery.data) : undefined;

  return useMemo(() => {
    const points = [...(state?.points ?? [])];
    points.sort((left, right) => (right.iter !== left.iter ? right.iter - left.iter : right.id.localeCompare(left.id)));
    return points;
  }, [state?.points, pulse]);
}
